import { isPinned, squareName, TEAM_NAMES, type Piece, type PieceType } from '@halvard/rules';
import { CENTER_SUMMIT, type BoardHint, type HintKind, type SummitMark } from './types.js';

export const PIECE_NAMES: Readonly<Record<PieceType, string>> = {
  B: 'Beacon',
  C: 'Climber',
  X: 'Ibex',
  L: 'Lookout',
  M: 'Mason',
};

const HINT_LABELS: Readonly<Record<HintKind, string>> = {
  move: 'move here',
  capture: 'capture',
  pin: 'pin',
  raise: 'raise',
  lower: 'lower',
};

/** "Ivory Climber" */
export function pieceName(piece: Pick<Piece, 'team' | 'type'>): string {
  return `${TEAM_NAMES[piece.team]} ${PIECE_NAMES[piece.type]}`;
}

/**
 * Accessible description of a square, e.g. "Ivory Climber on c1, height 1, pinned, capture".
 */
export function squareLabel(options: {
  r: number;
  c: number;
  heights: number[][];
  pieces: readonly Piece[];
  ply: number;
  hints?: readonly BoardHint[];
  selectedId?: string | null;
  /** Fog of war: squares the viewer can see. */
  visible?: boolean[][] | null;
  /** Summits on the board (defaults to e5). */
  summits?: readonly SummitMark[];
}): string {
  const { r, c, heights, pieces, ply } = options;
  const name = squareName(r, c);
  const height = heights[r]?.[c] ?? 0;
  const piece = pieces.find((p) => p.alive && p.r === r && p.c === c);
  const parts = [
    piece ? `${pieceName(piece)} on ${name}, height ${height}` : `${name}, height ${height}`,
  ];
  const summit = (options.summits ?? CENTER_SUMMIT).find((s) => s.r === r && s.c === c);
  if (summit) parts.push(summit.team ? `${TEAM_NAMES[summit.team]} summit` : 'summit');
  if (options.visible && options.visible[r]?.[c] !== true) parts.push('out of sight');
  if (piece && isPinned({ ply }, piece)) parts.push('pinned');
  if (piece && piece.id === options.selectedId) parts.push('selected');
  for (const hint of options.hints ?? []) {
    if (hint.r === r && hint.c === c) parts.push(HINT_LABELS[hint.kind]);
  }
  return parts.join(', ');
}
